import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  DrugInformation,
  DrugInteraction,
  AllergyAlert,
  ClinicalAlert,
  DosageCalculation,
  ClinicalGuideline,
  OrderAppropriatenessCheck,
  CDSConfiguration,
  AlertType,
  AlertSeverity,
  AlertStatus,
  InteractionSeverity,
} from "../types/clinicalDecisionSupport";

interface ClinicalDecisionSupportStore {
  // Drug Database
  drugs: DrugInformation[];
  interactions: DrugInteraction[];
  guidelines: ClinicalGuideline[];

  // Alerts
  alerts: ClinicalAlert[];
  activeAlerts: ClinicalAlert[];

  // Patient Context
  patientAllergies: Record<string, AllergyAlert[]>; // patientId -> allergies
  patientCurrentMedications: Record<string, string[]>; // patientId -> drugIds

  // Configuration
  configuration: CDSConfiguration;

  // Drug Database Methods
  getDrug: (drugId: string) => DrugInformation | undefined;
  searchDrugs: (query: string) => Promise<DrugInformation[]>;
  addDrug: (drug: DrugInformation) => void;

  // Interaction Checking
  checkDrugInteractions: (drugIds: string[]) => DrugInteraction[];
  checkNewDrugInteraction: (
    newDrugId: string,
    existingDrugIds: string[]
  ) => DrugInteraction[];

  // Allergy Checking
  checkAllergies: (patientId: string, drugId: string) => AllergyAlert | null;
  addPatientAllergy: (patientId: string, allergy: AllergyAlert) => void;
  getPatientAllergies: (patientId: string) => AllergyAlert[];

  // Dosage Calculation
  calculateDosage: (params: {
    drugId: string;
    patientWeight?: number;
    patientAge?: number;
    patientBSA?: number;
    renalFunction?: { creatinine: number; creatinineClearance: number };
    hepaticFunction?: "normal" | "mild" | "moderate" | "severe";
    requestedDose: { amount: number; unit: string; frequency: string };
  }) => DosageCalculation;

  // Alert Management
  createAlert: (
    alert: Omit<ClinicalAlert, "id" | "triggeredAt">
  ) => ClinicalAlert;
  acknowledgeAlert: (alertId: string, userId: string) => void;
  overrideAlert: (alertId: string, userId: string, reason: string) => void;
  resolveAlert: (alertId: string) => void;
  getActiveAlertsForPatient: (patientId: string) => ClinicalAlert[];
  getAlertsByType: (type: AlertType) => ClinicalAlert[];

  // Order Appropriateness
  checkOrderAppropriateness: (
    orderId: string,
    drugId: string,
    patientId: string
  ) => OrderAppropriatenessCheck;

  // Comprehensive Drug Check
  performComprehensiveDrugCheck: (params: {
    patientId: string;
    drugId: string;
    dose: { amount: number; unit: string; frequency: string };
    patientWeight?: number;
    patientAge?: number;
    renalFunction?: { creatinine: number; creatinineClearance: number };
    hepaticFunction?: "normal" | "mild" | "moderate" | "severe";
  }) => {
    interactions: DrugInteraction[];
    allergyAlert: AllergyAlert | null;
    dosageCalculation: DosageCalculation;
    alerts: ClinicalAlert[];
  };

  // Configuration
  updateConfiguration: (config: Partial<CDSConfiguration>) => void;

  // Utilities
  clearAlerts: () => void;
  getSeverityLevel: (severity: InteractionSeverity) => number;
  generateMockData: () => void;
}

export const useClinicalDecisionSupportStore =
  create<ClinicalDecisionSupportStore>()(
    persist(
      (set, get) => ({
        // Initial State
        drugs: [],
        interactions: [],
        guidelines: [],
        alerts: [],
        activeAlerts: [],
        patientAllergies: {},
        patientCurrentMedications: {},
        configuration: {
          enableDrugInteractionChecking: true,
          enableAllergyChecking: true,
          enableDosageValidation: true,
          enableDuplicateTherapyChecking: true,
          enableCostAlerts: true,
          enableFormularyAlerts: true,
          minimumInteractionSeverity: "minor",
          minimumAlertSeverity: "low",
          blockCriticalInteractions: true,
          requireAcknowledgmentForHighSeverity: true,
          allowOverride: true,
          requireOverrideJustification: true,
          notifyPharmacist: true,
          notifyPrescriber: true,
          notifyNurse: false,
          integrationEnabled: false,
          updatedAt: new Date().toISOString(),
          updatedBy: "system",
        },

        // Drug Database Methods
        getDrug: (drugId) => {
          return get().drugs.find((d) => d.id === drugId);
        },

        searchDrugs: async (query) => {
          const lowerQuery = query.toLowerCase();

          // First, search local CDS drugs (with full clinical data)
          const localDrugs = get().drugs.filter(
            (drug) =>
              drug.genericName.en.toLowerCase().includes(lowerQuery) ||
              drug.genericName.ar?.includes(query) ||
              drug.brandNames.some(
                (brand) =>
                  brand.en.toLowerCase().includes(lowerQuery) ||
                  brand.ar?.includes(query)
              )
          );

          try {
            // Also search RxNorm API for broader coverage
            const { searchDrugs: searchRxNorm } = await import('../utils/drugSearchApi');
            const rxnormResults = await searchRxNorm(query, 15);

            // Convert RxNorm results to basic DrugInformation format
            const rxnormDrugs: DrugInformation[] = rxnormResults.map(drug => {
              const isBrand = drug.tty === 'SBD' || drug.tty === 'SBDG';

              return {
                id: drug.rxcui,
                genericName: { en: drug.name, ar: drug.name },
                brandNames: isBrand ? [{ en: drug.name, ar: drug.name }] : [],
                drugClass: 'unknown',
                routes: ['oral'], // Default
                dosageForms: ['tablet'], // Default
                // No detailed clinical data from RxNorm - would need to be added separately
                contraindications: [],
                warnings: [],
                adverseEffects: [],
                requiredMonitoring: [],
              } as unknown as DrugInformation;
            });

            // Combine results, prioritizing local drugs (with full CDS data)
            // Filter out duplicates from RxNorm that already exist in local drugs
            const localDrugIds = new Set(localDrugs.map(d => d.id.toLowerCase()));
            const uniqueRxNormDrugs = rxnormDrugs.filter(
              d => !localDrugIds.has(d.id.toLowerCase()) &&
                   !localDrugs.some(ld =>
                     ld.genericName.en.toLowerCase() === d.genericName.en.toLowerCase()
                   )
            );

            return [...localDrugs, ...uniqueRxNormDrugs].slice(0, 20);
          } catch (error) {
            console.error('Error searching RxNorm API:', error);
            // Fallback to just local drugs
            return localDrugs;
          }
        },

        addDrug: (drug) => {
          set((state) => ({
            drugs: [...state.drugs, drug],
          }));
        },

        // Interaction Checking
        checkDrugInteractions: (drugIds) => {
          const { interactions, configuration } = get();
          if (!configuration.enableDrugInteractionChecking) return [];

          const foundInteractions: DrugInteraction[] = [];

          for (let i = 0; i < drugIds.length; i++) {
            for (let j = i + 1; j < drugIds.length; j++) {
              const interaction = interactions.find(
                (int) =>
                  (int.drug1Id === drugIds[i] && int.drug2Id === drugIds[j]) ||
                  (int.drug1Id === drugIds[j] && int.drug2Id === drugIds[i])
              );
              if (interaction) {
                foundInteractions.push(interaction);
              }
            }
          }

          return foundInteractions.filter(
            (int) =>
              get().getSeverityLevel(int.severity) >=
              get().getSeverityLevel(configuration.minimumInteractionSeverity)
          );
        },

        checkNewDrugInteraction: (newDrugId, existingDrugIds) => {
          return get().checkDrugInteractions([newDrugId, ...existingDrugIds]);
        },

        // Allergy Checking
        checkAllergies: (patientId, drugId) => {
          const { patientAllergies, configuration } = get();
          if (!configuration.enableAllergyChecking) return null;

          const allergies = patientAllergies[patientId] || [];
          const drug = get().getDrug(drugId);
          if (!drug) return null;

          // Check for exact drug match
          const exactMatch = allergies.find(
            (allergy) => allergy.allergen === drug.genericName.en
          );
          if (exactMatch) return exactMatch;

          // Check for drug class allergies (simplified)
          const classMatch = allergies.find((allergy) =>
            allergy.allergen.toLowerCase().includes(drug.drugClass)
          );
          if (classMatch) return classMatch;

          return null;
        },

        addPatientAllergy: (patientId, allergy) => {
          set((state) => ({
            patientAllergies: {
              ...state.patientAllergies,
              [patientId]: [
                ...(state.patientAllergies[patientId] || []),
                allergy,
              ],
            },
          }));
        },

        getPatientAllergies: (patientId) => {
          return get().patientAllergies[patientId] || [];
        },

        // Dosage Calculation
        calculateDosage: (params) => {
          const {
            drugId,
            patientWeight,
            patientAge,
            renalFunction,
            hepaticFunction,
            requestedDose,
          } = params;
          const drug = get().getDrug(drugId);

          if (!drug) {
            throw new Error("Drug not found");
          }

          const calculation: DosageCalculation = {
            drugId,
            drugName: drug.genericName.en,
            patientWeight,
            patientAge,
            renalFunction,
            hepaticFunction,
            calculatedDose: { ...requestedDose, route: drug.routes[0] },
            isWithinRange: true,
            adjustmentNeeded: false,
            calculationMethod: "standard",
            calculatedAt: new Date().toISOString(),
            calculatedBy: "system",
          };

          // Check if dose is within range
          const minDose = drug.adultDoseRange.min;
          const maxDose = drug.adultDoseRange.max;

          if (
            requestedDose.amount < minDose ||
            requestedDose.amount > maxDose
          ) {
            calculation.isWithinRange = false;
            calculation.adjustmentNeeded = true;
            calculation.adjustmentReason = `Dose outside recommended range (${minDose}-${maxDose} ${drug.adultDoseRange.unit})`;
            calculation.recommendedDose = {
              amount: minDose,
              unit: drug.adultDoseRange.unit,
              frequency: drug.adultDoseRange.frequency,
            };
          }

          // Check renal adjustment
          if (renalFunction && drug.renalDosing) {
            const adjustment = drug.renalDosing.find(
              (rd) => renalFunction.creatinineClearance < rd.creatinineClearance
            );
            if (adjustment) {
              calculation.adjustmentNeeded = true;
              calculation.adjustmentReason = `Renal dose adjustment needed: ${adjustment.doseAdjustment}`;
            }
          }

          // Check hepatic adjustment
          if (
            hepaticFunction &&
            hepaticFunction !== "normal" &&
            drug.hepaticDosing
          ) {
            const adjustment = drug.hepaticDosing.find(
              (hd) => hd.severity === hepaticFunction
            );
            if (adjustment) {
              calculation.adjustmentNeeded = true;
              calculation.adjustmentReason = `Hepatic dose adjustment needed: ${adjustment.doseAdjustment}`;
            }
          }

          return calculation;
        },

        // Alert Management
        createAlert: (alertData) => {
          const alert: ClinicalAlert = {
            ...alertData,
            id: `alert-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`,
            triggeredAt: new Date().toISOString(),
          };

          set((state) => ({
            alerts: [...state.alerts, alert],
            activeAlerts:
              alert.status === "active"
                ? [...state.activeAlerts, alert]
                : state.activeAlerts,
          }));

          return alert;
        },

        acknowledgeAlert: (alertId, userId) => {
          set((state) => ({
            alerts: state.alerts.map((alert) =>
              alert.id === alertId
                ? {
                    ...alert,
                    status: "acknowledged" as AlertStatus,
                    acknowledgedBy: userId,
                    acknowledgedAt: new Date().toISOString(),
                  }
                : alert
            ),
            activeAlerts: state.activeAlerts.filter(
              (alert) => alert.id !== alertId
            ),
          }));
        },

        overrideAlert: (alertId, userId, reason) => {
          set((state) => ({
            alerts: state.alerts.map((alert) =>
              alert.id === alertId
                ? {
                    ...alert,
                    status: "overridden" as AlertStatus,
                    overriddenBy: userId,
                    overriddenAt: new Date().toISOString(),
                    overrideReason: reason,
                  }
                : alert
            ),
            activeAlerts: state.activeAlerts.filter(
              (alert) => alert.id !== alertId
            ),
          }));
        },

        resolveAlert: (alertId) => {
          set((state) => ({
            alerts: state.alerts.map((alert) =>
              alert.id === alertId
                ? {
                    ...alert,
                    status: "resolved" as AlertStatus,
                    resolvedAt: new Date().toISOString(),
                  }
                : alert
            ),
            activeAlerts: state.activeAlerts.filter(
              (alert) => alert.id !== alertId
            ),
          }));
        },

        getActiveAlertsForPatient: (patientId) => {
          return get().activeAlerts.filter(
            (alert) => alert.patientId === patientId
          );
        },

        getAlertsByType: (type) => {
          return get().alerts.filter((alert) => alert.type === type);
        },

        // Order Appropriateness
        checkOrderAppropriateness: (orderId, drugId, _patientId) => {
          const drug = get().getDrug(drugId);
          const concerns: OrderAppropriatenessCheck["concerns"] = [];

          if (!drug) {
            return {
              orderId,
              orderType: "medication",
              drugId,
              isAppropriate: false,
              concerns: [
                {
                  type: "unknown_drug",
                  severity: "high" as AlertSeverity,
                  description: {
                    en: "Drug not found in database",
                    ar: "الدواء غير موجود في قاعدة البيانات",
                  },
                  recommendation: {
                    en: "Verify drug information",
                    ar: "تحقق من معلومات الدواء",
                  },
                },
              ],
              checkedAt: new Date().toISOString(),
              checkedBy: "system",
            };
          }

          // Check formulary status (mock)
          const formularyStatus =
            Math.random() > 0.7 ? "preferred" : "non-preferred";
          const priorAuthRequired =
            formularyStatus === "non-preferred" && Math.random() > 0.5;

          if (priorAuthRequired) {
            concerns.push({
              type: "prior_authorization",
              severity: "medium" as AlertSeverity,
              description: {
                en: "Prior authorization required",
                ar: "مطلوب تفويض مسبق",
              },
              recommendation: {
                en: "Submit prior authorization request",
                ar: "قدم طلب التفويض المسبق",
              },
            });
          }

          return {
            orderId,
            orderType: "medication",
            drugId,
            isAppropriate: concerns.length === 0,
            concerns,
            formularyStatus,
            priorAuthorizationRequired: priorAuthRequired,
            checkedAt: new Date().toISOString(),
            checkedBy: "system",
          };
        },

        // Comprehensive Drug Check
        performComprehensiveDrugCheck: (params) => {
          const {
            patientId,
            drugId,
            dose,
            patientWeight,
            patientAge,
            renalFunction,
            hepaticFunction,
          } = params;
          const { configuration } = get();

          // Get patient's current medications
          const currentMeds = get().patientCurrentMedications[patientId] || [];

          // Check interactions
          const interactions = configuration.enableDrugInteractionChecking
            ? get().checkNewDrugInteraction(drugId, currentMeds)
            : [];

          // Check allergies
          const allergyAlert = configuration.enableAllergyChecking
            ? get().checkAllergies(patientId, drugId)
            : null;

          // Calculate dosage
          const dosageCalculation = get().calculateDosage({
            drugId,
            patientWeight,
            patientAge,
            renalFunction,
            hepaticFunction,
            requestedDose: dose,
          });

          // Generate alerts
          const alerts: ClinicalAlert[] = [];

          // Interaction alerts
          interactions.forEach((interaction) => {
            const severity: AlertSeverity =
              interaction.severity === "major"
                ? "critical"
                : interaction.severity === "moderate"
                ? "high"
                : "medium";

            alerts.push(
              get().createAlert({
                type: "drugInteraction",
                severity,
                status: "active",
                title: {
                  en: "Drug Interaction Detected",
                  ar: "تم اكتشاف تفاعل دوائي",
                },
                message: interaction.description,
                recommendation: interaction.management,
                patientId,
                drugId,
                interactionDetails: interaction,
                requiresAcknowledgment:
                  severity === "critical" || severity === "high",
                canBeOverridden: configuration.allowOverride,
                triggeredBy: "system",
              })
            );
          });

          // Allergy alert
          if (allergyAlert) {
            alerts.push(
              get().createAlert({
                type: "allergy",
                severity: "critical",
                status: "active",
                title: { en: "Allergy Alert", ar: "تنبيه الحساسية" },
                message: {
                  en: `Patient is allergic to ${allergyAlert.allergen}`,
                  ar: `المريض لديه حساسية من ${allergyAlert.allergen}`,
                },
                recommendation: {
                  en: "Do not administer. Consider alternative medication.",
                  ar: "لا تعطي الدواء. فكر في دواء بديل.",
                },
                patientId,
                drugId,
                allergyDetails: allergyAlert,
                requiresAcknowledgment: true,
                canBeOverridden: false,
                triggeredBy: "system",
              })
            );
          }

          // Dosage alert
          if (dosageCalculation.adjustmentNeeded) {
            const severity: AlertSeverity = dosageCalculation.isWithinRange
              ? "medium"
              : "high";
            alerts.push(
              get().createAlert({
                type: "dosage",
                severity,
                status: "active",
                title: {
                  en: "Dosage Adjustment Needed",
                  ar: "يلزم تعديل الجرعة",
                },
                message: {
                  en:
                    dosageCalculation.adjustmentReason ||
                    "Dose adjustment recommended",
                  ar:
                    dosageCalculation.adjustmentReason || "يوصى بتعديل الجرعة",
                },
                recommendation: {
                  en: `Recommended dose: ${dosageCalculation.recommendedDose?.amount} ${dosageCalculation.recommendedDose?.unit}`,
                  ar: `الجرعة الموصى بها: ${dosageCalculation.recommendedDose?.amount} ${dosageCalculation.recommendedDose?.unit}`,
                },
                patientId,
                drugId,
                dosageDetails: dosageCalculation,
                requiresAcknowledgment: severity === "high",
                canBeOverridden: configuration.allowOverride,
                triggeredBy: "system",
              })
            );
          }

          return {
            interactions,
            allergyAlert,
            dosageCalculation,
            alerts,
          };
        },

        // Configuration
        updateConfiguration: (config) => {
          set((state) => ({
            configuration: {
              ...state.configuration,
              ...config,
              updatedAt: new Date().toISOString(),
            },
          }));
        },

        // Utilities
        clearAlerts: () => {
          set({ alerts: [], activeAlerts: [] });
        },

        getSeverityLevel: (severity: InteractionSeverity): number => {
          const levels = { minor: 1, moderate: 2, major: 3 };
          return levels[severity];
        },

        // Generate Mock Data
        generateMockData: () => {
          // Common medications with comprehensive data
          const mockDrugs: DrugInformation[] = [
            {
              id: "warfarin",
              genericName: { en: "Warfarin", ar: "وارفارين" },
              brandNames: [{ en: "Coumadin", ar: "كومادين" }],
              drugClass: "anticoagulant",
              routes: ["oral"],
              dosageForms: ["tablet"],
              adultDoseRange: {
                min: 2,
                max: 10,
                unit: "mg",
                frequency: "daily",
              },
              contraindications: [
                { en: "Active bleeding", ar: "نزيف نشط" },
                { en: "Pregnancy", ar: "الحمل" },
              ],
              warnings: [
                {
                  en: "Requires regular INR monitoring",
                  ar: "يتطلب مراقبة منتظمة لـ INR",
                },
                {
                  en: "Numerous drug interactions",
                  ar: "تفاعلات دوائية عديدة",
                },
              ],
              adverseEffects: [
                { en: "Bleeding", ar: "نزيف" },
                { en: "Bruising", ar: "كدمات" },
              ],
              pregnancyCategory: "X",
              requiredMonitoring: [
                {
                  parameter: "INR",
                  frequency: "Weekly initially, then monthly",
                  reason: {
                    en: "Monitor anticoagulation",
                    ar: "مراقبة تأثير مضاد التخثر",
                  },
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "aspirin",
              genericName: { en: "Aspirin", ar: "أسبرين" },
              brandNames: [{ en: "Bufferin", ar: "بوفرين" }],
              drugClass: "analgesic",
              routes: ["oral"],
              dosageForms: ["tablet"],
              adultDoseRange: {
                min: 81,
                max: 325,
                unit: "mg",
                frequency: "daily",
              },
              contraindications: [
                { en: "Active peptic ulcer", ar: "قرحة هضمية نشطة" },
                { en: "Severe renal impairment", ar: "قصور كلوي شديد" },
              ],
              warnings: [
                { en: "Increased bleeding risk", ar: "خطر النزيف المتزايد" },
              ],
              adverseEffects: [
                { en: "GI upset", ar: "اضطراب الجهاز الهضمي" },
                { en: "Bleeding", ar: "نزيف" },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "metformin",
              genericName: { en: "Metformin", ar: "ميتفورمين" },
              brandNames: [{ en: "Glucophage", ar: "جلوكوفاج" }],
              drugClass: "antidiabetic",
              routes: ["oral"],
              dosageForms: ["tablet"],
              adultDoseRange: {
                min: 500,
                max: 2000,
                unit: "mg",
                frequency: "twice daily",
              },
              renalDosing: [
                {
                  creatinineClearance: 30,
                  doseAdjustment: "Reduce dose by 50%",
                },
                { creatinineClearance: 15, doseAdjustment: "Contraindicated" },
              ],
              contraindications: [
                {
                  en: "Severe renal impairment (eGFR <30)",
                  ar: "قصور كلوي شديد",
                },
                { en: "Acute metabolic acidosis", ar: "حماض استقلابي حاد" },
              ],
              warnings: [
                { en: "Risk of lactic acidosis", ar: "خطر الحماض اللبني" },
              ],
              adverseEffects: [
                { en: "Nausea", ar: "غثيان" },
                { en: "Diarrhea", ar: "إسهال" },
              ],
              requiredMonitoring: [
                {
                  parameter: "Renal function",
                  frequency: "Every 3-6 months",
                  reason: {
                    en: "Assess for accumulation",
                    ar: "تقييم التراكم",
                  },
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "lisinopril",
              genericName: { en: "Lisinopril", ar: "ليزينوبريل" },
              brandNames: [{ en: "Zestril", ar: "زيستريل" }],
              drugClass: "antihypertensive",
              routes: ["oral"],
              dosageForms: ["tablet"],
              adultDoseRange: {
                min: 5,
                max: 40,
                unit: "mg",
                frequency: "daily",
              },
              renalDosing: [
                {
                  creatinineClearance: 30,
                  doseAdjustment: "Start with 2.5mg daily",
                },
              ],
              contraindications: [
                { en: "Pregnancy", ar: "الحمل" },
                { en: "History of angioedema", ar: "تاريخ من الوذمة الوعائية" },
              ],
              warnings: [
                {
                  en: "May cause hyperkalemia",
                  ar: "قد يسبب ارتفاع البوتاسيوم",
                },
                { en: "Monitor renal function", ar: "مراقبة وظائف الكلى" },
              ],
              adverseEffects: [
                { en: "Dry cough", ar: "سعال جاف" },
                { en: "Dizziness", ar: "دوخة" },
              ],
              pregnancyCategory: "D",
              requiredMonitoring: [
                {
                  parameter: "Potassium",
                  frequency: "Baseline and periodically",
                  reason: {
                    en: "Monitor for hyperkalemia",
                    ar: "مراقبة ارتفاع البوتاسيوم",
                  },
                },
                {
                  parameter: "Renal function",
                  frequency: "Baseline and periodically",
                  reason: {
                    en: "Assess kidney function",
                    ar: "تقييم وظائف الكلى",
                  },
                },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            {
              id: "amoxicillin",
              genericName: { en: "Amoxicillin", ar: "أموكسيسيلين" },
              brandNames: [{ en: "Amoxil", ar: "أموكسيل" }],
              drugClass: "antibiotic",
              routes: ["oral"],
              dosageForms: ["capsule", "tablet", "suspension"],
              adultDoseRange: {
                min: 250,
                max: 1000,
                unit: "mg",
                frequency: "three times daily",
              },
              pediatricDoseRange: {
                min: 20,
                max: 50,
                unit: "mg/kg/day",
                frequency: "divided doses",
                weightBased: true,
              },
              contraindications: [
                { en: "Penicillin allergy", ar: "حساسية البنسلين" },
              ],
              warnings: [
                {
                  en: "May cause allergic reactions",
                  ar: "قد يسبب ردود فعل تحسسية",
                },
              ],
              adverseEffects: [
                { en: "Diarrhea", ar: "إسهال" },
                { en: "Nausea", ar: "غثيان" },
                { en: "Rash", ar: "طفح جلدي" },
              ],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];

          // Drug interactions
          const mockInteractions: DrugInteraction[] = [
            {
              id: "int-warfarin-aspirin",
              drug1Id: "warfarin",
              drug1Name: { en: "Warfarin", ar: "وارفارين" },
              drug2Id: "aspirin",
              drug2Name: { en: "Aspirin", ar: "أسبرين" },
              severity: "major",
              type: "pharmacodynamic",
              description: {
                en: "Concurrent use significantly increases bleeding risk",
                ar: "الاستخدام المتزامن يزيد بشكل كبير من خطر النزيف",
              },
              clinicalEffects: {
                en: "Additive anticoagulant and antiplatelet effects leading to increased bleeding risk",
                ar: "تأثيرات إضافية مضادة للتخثر ومضادة للصفائح تؤدي إلى زيادة خطر النزيف",
              },
              mechanism: {
                en: "Both drugs affect hemostasis through different mechanisms",
                ar: "كلا الدواءين يؤثران على الإرقاء من خلال آليات مختلفة",
              },
              management: {
                en: "Avoid combination if possible. If necessary, use lowest effective aspirin dose (81mg) and monitor INR closely.",
                ar: "تجنب المزيج إن أمكن. إذا لزم الأمر، استخدم أقل جرعة فعالة من الأسبرين (81 ملغ) وراقب INR عن كثب.",
              },
              evidence: "established",
            },
            {
              id: "int-lisinopril-metformin",
              drug1Id: "lisinopril",
              drug1Name: { en: "Lisinopril", ar: "ليزينوبريل" },
              drug2Id: "metformin",
              drug2Name: { en: "Metformin", ar: "ميتفورمين" },
              severity: "moderate",
              type: "pharmacodynamic",
              description: {
                en: "ACE inhibitors may affect glucose metabolism",
                ar: "مثبطات الإنزيم المحول للأنجيوتنسين قد تؤثر على استقلاب الجلوكوز",
              },
              clinicalEffects: {
                en: "May enhance hypoglycemic effect of metformin",
                ar: "قد يعزز تأثير خفض السكر للميتفورمين",
              },
              mechanism: {
                en: "ACE inhibitors may improve insulin sensitivity",
                ar: "مثبطات الإنزيم المحول للأنجيوتنسين قد تحسن حساسية الأنسولين",
              },
              management: {
                en: "Monitor blood glucose levels, especially when initiating therapy. May need to adjust metformin dose.",
                ar: "راقب مستويات السكر في الدم، خاصة عند بدء العلاج. قد تحتاج إلى تعديل جرعة الميتفورمين.",
              },
              evidence: "probable",
            },
          ];

          // Sample patient allergies
          const sampleAllergies: Record<string, AllergyAlert[]> = {
            "patient-1": [
              {
                id: "allergy-1",
                patientId: "patient-1",
                allergen: "Penicillin",
                allergenType: "drug",
                reaction: "Anaphylaxis",
                severity: "life-threatening",
                onsetDate: "2020-03-15",
                verifiedBy: "Dr. Smith",
              },
            ],
            "1": [
              {
                id: "allergy-1-sarah",
                patientId: "1",
                allergen: "Penicillin",
                allergenType: "drug",
                reaction: "Anaphylaxis",
                severity: "life-threatening",
                onsetDate: "2020-03-15",
                verifiedBy: "Dr. Smith",
              },
            ],
          };

          // Sample current medications for patients
          const sampleMedications: Record<string, string[]> = {
            "2": ["warfarin", "lisinopril", "metformin"], // Michael Chen - on anticoagulant, antihypertensive, antidiabetic
            "4": ["warfarin", "aspirin"], // James Wilson - dangerous drug interaction (Warfarin + Aspirin)
          };

          // Generate sample clinical alerts
          const sampleAlerts: ClinicalAlert[] = [
            // Major drug interaction alert for patient 4
            {
              id: "alert-interaction-1",
              type: "drugInteraction",
              severity: "critical",
              status: "active",
              title: {
                en: "Major Drug Interaction: Warfarin + Aspirin",
                ar: "تفاعل دوائي رئيسي: وارفارين + أسبرين",
              },
              message: {
                en: "Concurrent use of Warfarin and Aspirin significantly increases bleeding risk.",
                ar: "الاستخدام المتزامن للوارفارين والأسبرين يزيد بشكل كبير من خطر النزيف.",
              },
              recommendation: {
                en: "Consider discontinuing aspirin or using lowest effective dose (81mg). Monitor INR closely and watch for signs of bleeding.",
                ar: "يُنصح بإيقاف الأسبرين أو استخدام أقل جرعة فعالة (81 ملغ). راقب INR عن كثب وراقب علامات النزيف.",
              },
              patientId: "4",
              drugId: "warfarin",
              drugName: "Warfarin",
              interactionDetails: mockInteractions[0],
              requiresAcknowledgment: true,
              canBeOverridden: true,
              triggeredAt: new Date().toISOString(),
              triggeredBy: "CDS System",
            },
            // Moderate interaction alert for patient 2
            {
              id: "alert-interaction-2",
              type: "drugInteraction",
              severity: "medium",
              status: "active",
              title: {
                en: "Moderate Interaction: Lisinopril + Metformin",
                ar: "تفاعل متوسط: ليسينوبريل + ميتفورمين",
              },
              message: {
                en: "Lisinopril may enhance the hypoglycemic effect of Metformin.",
                ar: "قد يعزز ليسينوبريل التأثير الخافض للسكر للميتفورمين.",
              },
              recommendation: {
                en: "Monitor blood glucose more frequently. Educate patient on hypoglycemia symptoms.",
                ar: "راقب جلوكوز الدم بشكل متكرر. قم بتثقيف المريض حول أعراض نقص السكر في الدم.",
              },
              patientId: "2",
              drugId: "lisinopril",
              drugName: "Lisinopril",
              interactionDetails: mockInteractions[1],
              requiresAcknowledgment: true,
              canBeOverridden: true,
              triggeredAt: new Date().toISOString(),
              triggeredBy: "CDS System",
            },
            // Allergy alert for patient 1
            {
              id: "alert-allergy-1",
              type: "allergy",
              severity: "critical",
              status: "active",
              title: {
                en: "Severe Allergy Alert: Penicillin",
                ar: "تنبيه حساسية شديدة: بنسلين",
              },
              message: {
                en: "Patient has documented life-threatening anaphylaxis to Penicillin. DO NOT prescribe Amoxicillin or other penicillin-based antibiotics.",
                ar: "المريض لديه حساسية مفرطة مهددة للحياة موثقة للبنسلين. لا تصف أموكسيسيلين أو المضادات الحيوية الأخرى القائمة على البنسلين.",
              },
              recommendation: {
                en: "Use alternative antibiotics such as azithromycin, fluoroquinolones, or cephalosporins (with caution for cross-reactivity).",
                ar: "استخدم مضادات حيوية بديلة مثل أزيثروميسين أو فلوروكينولونات أو سيفالوسبورينات (مع توخي الحذر من التفاعل المتبادل).",
              },
              patientId: "1",
              drugId: "amoxicillin",
              drugName: "Amoxicillin",
              allergyDetails: sampleAllergies["1"][0],
              requiresAcknowledgment: true,
              canBeOverridden: false,
              triggeredAt: new Date(
                Date.now() - 2 * 60 * 60 * 1000
              ).toISOString(), // 2 hours ago
              triggeredBy: "CDS System",
            },
            // Monitoring alert for patient 4
            {
              id: "alert-monitoring-1",
              type: "monitoring",
              severity: "high",
              status: "active",
              title: {
                en: "Required Monitoring: Warfarin INR",
                ar: "مراقبة مطلوبة: INR الوارفارين",
              },
              message: {
                en: "Patient is on Warfarin therapy. Regular INR monitoring is required to ensure therapeutic range and prevent complications.",
                ar: "المريض يتناول علاج الوارفارين. المراقبة المنتظمة لـ INR مطلوبة لضمان النطاق العلاجي ومنع المضاعفات.",
              },
              recommendation: {
                en: "Order INR test. Target INR range: 2.0-3.0. Monitor weekly initially, then monthly when stable.",
                ar: "اطلب فحص INR. النطاق المستهدف لـ INR: 2.0-3.0. راقب أسبوعيًا في البداية، ثم شهريًا عند الاستقرار.",
              },
              patientId: "4",
              drugId: "warfarin",
              drugName: "Warfarin",
              requiresAcknowledgment: false,
              canBeOverridden: false,
              triggeredAt: new Date().toISOString(),
              triggeredBy: "CDS System",
            },
          ];

          // Filter active alerts for the activeAlerts array
          const activeSampleAlerts = sampleAlerts.filter(
            (alert) => alert.status === "active"
          );

          set({
            drugs: mockDrugs,
            interactions: mockInteractions,
            patientAllergies: sampleAllergies,
            patientCurrentMedications: sampleMedications,
            alerts: sampleAlerts,
            activeAlerts: activeSampleAlerts,
          });
        },
      }),
      {
        name: "clinical-decision-support-storage",
        partialize: (state) => ({
          drugs: state.drugs,
          interactions: state.interactions,
          guidelines: state.guidelines,
          alerts: state.alerts,
          patientAllergies: state.patientAllergies,
          patientCurrentMedications: state.patientCurrentMedications,
          configuration: state.configuration,
        }),
      }
    )
  );
